import { debounceTime, Subject } from 'rxjs';
import { PartModel } from '../services/project-controller/project-controller';
import { moveItemInArray } from '@angular/cdk/drag-drop';

export interface StateManagerInfo {
  setting: Array<SettingInfo>;
  partModels: Array<PartModel>;
  stickers: Array<StickerInfo>;
  deleteInfo?: StateManagerInfo;
  addInfo?: StateManagerInfo;
}

export interface SettingInfo {
  material: string;
  style: {
    type: 'color' | 'image';
    value: string;
  };
  isMatte: boolean;
}

export interface StickerInfo {
  attachMeshNames: Array<string>;
  stickerUrl: string;
  position: {
    x: number;
    y: number;
    z: number;
  };
  rotation: {
    x: number;
    y: number;
    z: number;
  };
  renderOrder: number;
  scale: number;
  scaleRatio: number;
  isDualSided: boolean;
}

export class StateManager {
  private stateHistory: StateManagerInfo[] = [];

  private currentStateIndex: number = -1;
  private stateHistoryLimit: number = 100;

  private styleSaveDurationSubscription: Subject<Array<SettingInfo>> = new Subject<
    Array<SettingInfo>
  >();

  constructor() {
    this.init();
  }

  public init(): void {
    this.stateHistory = [];
    this.stateHistory.push({
      setting: [],
      partModels: [],
      stickers: [],
    });
    this.currentStateIndex = 0;
    this.styleSaveDurationSubscription.pipe(debounceTime(1000)).subscribe((setting) => {
      const status = this.copy();
      status.setting = setting;
      this.addState(status);
    });
  }

  public destroy(): void {
    this.styleSaveDurationSubscription.complete();
    this.styleSaveDurationSubscription.unsubscribe();
  }

  public isUndo(): boolean {
    return this.currentStateIndex > 0;
  }

  public isRedo(): boolean {
    return this.currentStateIndex < this.stateHistory.length - 1;
  }

  public setSettings(settings: Array<SettingInfo>): void {
    this.styleSaveDurationSubscription.next(settings);
  }

  public addModel(model: PartModel): void {
    const status = this.copy();
    status.partModels = status.partModels.filter((_model) => _model.category !== model.category);
    status.partModels.push(model);
    this.addState(status);
  }

  public removeModel(model: PartModel): void {
    const status = this.copy();
    status.partModels = status.partModels.filter((_model) => _model !== model);
    this.addState(status);
  }

  public addSticker(sticker: StickerInfo): void {
    const status = this.copy();
    status.stickers.push(sticker);
    this.addState(status);
  }

  public updateDeleteStickerAttachMesh(index: number, name: string): void {
    const status = this.copy();
    status.stickers[index].attachMeshNames = status.stickers[index].attachMeshNames.filter(
      (stickerName) => stickerName !== name,
    );
    this.addState(status);
  }

  public moveSticker(from: number, to: number): void {
    const status = this.copy();
    moveItemInArray(status.stickers, from, to);

    status.stickers.forEach((info, index) => {
      info.renderOrder = status.stickers.length - index;
    });

    this.addState(status);
  }

  public removeSticker(index: number): void {
    const status = this.copy();
    status.stickers = status.stickers.filter((sticker, i) => i !== index);
    this.addState(status);
  }

  public undo(): StateManagerInfo | null {
    if (this.currentStateIndex > 0) {
      this.currentStateIndex--;
      return this.diffrentState(this.currentStateIndex + 1, this.currentStateIndex);
    }
    return null;
  }

  public redo(): StateManagerInfo | null {
    if (this.currentStateIndex < this.stateHistory.length - 1) {
      this.currentStateIndex++;
      return this.diffrentState(this.currentStateIndex - 1, this.currentStateIndex);
    }
    return null;
  }

  public getSettings(): Array<SettingInfo> {
    console.log(this.stateHistory[this.currentStateIndex]);
    return this.stateHistory[this.currentStateIndex]?.setting;
  }

  public getPartModels(): Array<PartModel> {
    return this.stateHistory[this.currentStateIndex]?.partModels;
  }

  public getStickers(): Array<StickerInfo> {
    return this.stateHistory[this.currentStateIndex]?.stickers;
  }

  private diffrentState(beforeIndex: number, nextIndex: number): StateManagerInfo {
    const beforeState = this.stateHistory[beforeIndex];
    const nextState = this.stateHistory[nextIndex];

    const diffrentState: StateManagerInfo = {
      setting: [],
      partModels: [],
      stickers: [],
    };

    diffrentState.setting = this.stateHistory[beforeIndex].setting;
    diffrentState.partModels = this.stateHistory[beforeIndex].partModels;
    diffrentState.stickers = this.stateHistory[beforeIndex].stickers;
    diffrentState.deleteInfo = {
      setting: [],
      partModels: [],
      stickers: [],
    };

    diffrentState.addInfo = {
      setting: [],
      partModels: [],
      stickers: [],
    };

    diffrentState.deleteInfo.partModels = beforeState.partModels.filter((uuid) => {
      return !nextState.partModels.includes(uuid);
    });
    diffrentState.addInfo.partModels = nextState.partModels.filter((uuid) => {
      return !beforeState.partModels.includes(uuid);
    });

    diffrentState.deleteInfo.stickers = beforeState.stickers.filter((sticker) => {
      return !nextState.stickers.includes(sticker);
    });

    diffrentState.addInfo.stickers = nextState.stickers.filter((sticker) => {
      return !beforeState.stickers.includes(sticker);
    });

    return diffrentState;
  }

  private addState(state: StateManagerInfo): void {
    if (this.currentStateIndex < this.stateHistory.length - 1) {
      this.stateHistory = this.stateHistory.slice(0, this.currentStateIndex + 1);
    }

    if (this.stateHistory.length >= this.stateHistoryLimit) {
      this.stateHistory.shift();
      this.currentStateIndex--;
    }

    this.stateHistory.push(state);
    this.currentStateIndex++;

    console.log(this.stateHistory);
  }

  private copy(): StateManagerInfo {
    const copyState: StateManagerInfo = {
      setting: [],
      partModels: [],
      stickers: [],
    };

    copyState.setting = this.stateHistory[this.currentStateIndex].setting.map((setting) => {
      const copySetting: SettingInfo = {
        material: setting.material,
        style: {
          type: setting.style.type,
          value: setting.style.value,
        },
        isMatte: setting.isMatte,
      };

      return copySetting;
    });
    copyState.partModels = this.stateHistory[this.currentStateIndex].partModels.map((part) => part);
    copyState.stickers = this.stateHistory[this.currentStateIndex].stickers.map((sticker) => {
      const stickerInfo: StickerInfo = {
        attachMeshNames: sticker.attachMeshNames.map((name) => name),
        stickerUrl: sticker.stickerUrl,
        position: {
          x: sticker.position.x,
          y: sticker.position.y,
          z: sticker.position.z,
        },
        rotation: {
          x: sticker.rotation.x,
          y: sticker.rotation.y,
          z: sticker.rotation.z,
        },
        scaleRatio: sticker.scaleRatio,
        renderOrder: sticker.renderOrder,
        scale: sticker.scale,
        isDualSided: sticker.isDualSided,
      };

      return stickerInfo;
    });

    return copyState;
  }
}
